export const PANEL_TYPE = {
  FULL_PANEL: "full_panel",
  PART_OF_PANEL: "part_of_panel",
  NORMAL: "normal",
};

const PANEL_FULL_FLAG = 1;
const PANEL_MEMBER_FLAG = 2;

const normalizeSubPanelList = (rawSubPanelList) => {
  if (!Array.isArray(rawSubPanelList)) return [];
  const seen = new Set();
  const names = [];
  rawSubPanelList.forEach((entry) => {
    const name = String(entry ?? "").trim();
    if (!name || seen.has(name)) return;
    seen.add(name);
    names.push(name);
  });
  return names;
};

const isValidPanelDetailsEntry = (raw) => Boolean(raw && raw.is_panel_test);

const getPanelDetailsRawList = (test) => {
  const raw = test?.panel_details;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.filter(isValidPanelDetailsEntry);
  }
  return isValidPanelDetailsEntry(raw) ? [raw] : [];
};

const normalizePanelDetailEntry = (raw) => ({
  isPanelTestFlag: Number(raw.is_panel_test),
  parentDisplayName: raw.parent_display_name || null,
  textColor: raw.text_color || null,
  backgroundColor: raw.background_color || null,
  subPanelList: normalizeSubPanelList(raw.sub_panel_list),
});

const getPanelDetails = (test) => {
  const [firstEntry] = getPanelDetailsRawList(test);
  if (!firstEntry) {
    return null;
  }
  return normalizePanelDetailEntry(firstEntry);
};

export const getSubPanelList = (test) => {
  const details = getPanelDetails(test);
  return details?.subPanelList ?? [];
};

export const isPanelMember = (test) => {
  const details = getPanelDetails(test);
  return Boolean(details) && details.isPanelTestFlag === PANEL_MEMBER_FLAG;
};

export const isFullPanel = (test) => {
  const details = getPanelDetails(test);
  return Boolean(details) && details.isPanelTestFlag === PANEL_FULL_FLAG;
};

export const getPanelSearchMatches = (panelTest, searchQuery = "") => {
  if (!isFullPanel(panelTest)) {
    return { matchNames: [], matchCount: 0, totalCount: 0 };
  }
  const subList = getSubPanelList(panelTest);
  const totalCount = subList.length;
  const query = String(searchQuery || "").trim().toLowerCase();
  if (!query || totalCount === 0) {
    return { matchNames: [], matchCount: 0, totalCount };
  }
  const matchNames = subList.filter((name) => name.toLowerCase().includes(query));
  if (matchNames.length > 0) {
    return {
      matchNames,
      matchCount: matchNames.length,
      totalCount,
    };
  }
  const panelName = String(panelTest?.name || panelTest?.test_name || "").toLowerCase();
  if (!panelName.includes(query)) {
    return {
      matchNames: subList,
      matchCount: subList.length,
      totalCount: subList.length,
    };
  }
  return { matchNames: [], matchCount: 0, totalCount };
};

export const shouldShowPanelSubtestRequestPrompt = (tests, searchQuery = "") => {
  const query = String(searchQuery || "").trim().toLowerCase();
  if (!query) return false;

  const list = Array.isArray(tests) ? tests : [];
  const hasExactNameRow = list.some(
    (t) => String(t?.name || t?.test_name || "").trim().toLowerCase() === query,
  );
  if (hasExactNameRow) return false;

  return list.some((t) => {
    if (!isFullPanel(t)) return false;
    const panelName = String(t?.name || t?.test_name || "").trim().toLowerCase();
    if (!panelName || panelName === query) return false;
    return getPanelSearchMatches(t, query).matchCount > 0;
  });
};

export const getPanelInfo = (test) => {
  const details = getPanelDetails(test);
  if (!details) {
    return {
      type: PANEL_TYPE.NORMAL,
      parentDisplayName: null,
      textColor: null,
      backgroundColor: null,
      subPanelList: [],
    };
  }
  return {
    type: isFullPanel(test) ? PANEL_TYPE.FULL_PANEL : PANEL_TYPE.PART_OF_PANEL,
    ...details,
  };
};

export const getPanelInfoList = (test) => getPanelDetailsRawList(test).map((raw) => {
  const details = normalizePanelDetailEntry(raw);
  return {
    type: details.isPanelTestFlag === PANEL_FULL_FLAG
      ? PANEL_TYPE.FULL_PANEL
      : PANEL_TYPE.PART_OF_PANEL,
    ...details,
  };
});

const getPanelBadgeTextForInfo = (info, context = "browse") => {
  if (info.type === PANEL_TYPE.FULL_PANEL) return "Full Panel";
  if (info.type === PANEL_TYPE.PART_OF_PANEL) {
    return context === "selected" ? `Part of ${info.parentDisplayName}` : info.parentDisplayName;
  }
  return null;
};

export const getPanelBadgeText = (test, context = "browse") => {
  const info = getPanelInfo(test);
  return getPanelBadgeTextForInfo(info, context);
};

export const getPanelBadgeItems = (test, context = "browse") => getPanelInfoList(test)
  .map((info) => {
    const text = getPanelBadgeTextForInfo(info, context);
    if (!text) return null;
    return {
      text,
      textColor: info.textColor,
      backgroundColor: info.backgroundColor,
      panelType: info.type,
      icon: info.type !== PANEL_TYPE.FULL_PANEL,
    };
  })
  .filter(Boolean);

export const belongsToPanel = (test, panelName) => {
  const info = getPanelInfo(test);
  return info.type === PANEL_TYPE.PART_OF_PANEL && info.parentDisplayName === panelName;
};

export const isSamePanel = (testA, testB) => {
  const infoA = getPanelInfo(testA);
  const infoB = getPanelInfo(testB);
  return Boolean(infoA.parentDisplayName) && infoA.parentDisplayName === infoB.parentDisplayName;
};

const toArray = (testPool) => (testPool instanceof Map ? Array.from(testPool.values()) : Array.from(testPool || []));

export const getPanelMembers = (panelName, testPool) => toArray(testPool).filter(
  (t) => belongsToPanel(t, panelName),
);

export const findFullPanelTest = (panelName, testPool) => toArray(testPool).find(
  (t) => isFullPanel(t) && (t?.name || t?.test_name) === panelName,
);
